import { ArrowLeft, Plus, RefreshCcw, SignatureIcon, Trash, X } from "lucide-react"
import BaseModal from "../../../components/layout/BaseModal"
import { useRef, useState } from "react"
import BaseInputField from "../../../components/elements/BaseInputField"
import { Form } from "antd"
import BaseSelection from "../../../components/elements/BaseSelection"
import BaseInputSelection from "../../../components/elements/BaseInputSelection"
import { InputObject } from "../../../components/types"
import { getEmployeeNamesOnly } from "../../Mitarbeiter/api/apiCalls"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { GeneralQueryKeys } from "../../../api/constants"
import SignatureCanvas from "react-signature-canvas"
import BaseSectionHeadline from "../../../components/elements/BaseSectionHeadline"
import { useAddKeycollectionMutation, useDeleteKeycollectionMutation, useUpdateKeycollectionMutation } from "../api/services"
import { KeycollectionType } from "../types/types"
import dayjs from "dayjs"

const keycollections = [
    {
        id: 1,
        name: "Schlüsselbund 1",
        keyCount: 1,
        keys: "Hausschlüssel",
        currentOwner: "Hausverwaltung", // "Mitarbeiter", "Mieter"
        signature: "Unterschrift",
        history: [
            {
                timestamp: new Date(),
                changedBy: "Verwaltung",
                changedValue: "Schlüsselbund erstellt",
            },
            {
                timestamp: new Date(),
                changedBy: "Mitarbeiter X",
                changedValue: "Unterschrift hinzugefügt",
            },
        ],
    },
]

type KeyManagementModalProps = {
    modalId: number
    queryKey: string
    objectId: string
    keycollections: KeycollectionType[]
}

export default function KeyManagementModal(props: KeyManagementModalProps) {
    const [selectedKeycollection, setSelectedKeycollection] = useState<any>(null)
    const [form] = Form.useForm()
    const [currentOwnerState, setCurrentOwnerState] = useState<string>()
    const signatureRef = useRef<any>("")
    const [signature, setSignature] = useState<boolean>(false)
    const { data: employees } = useQuery({
        queryKey: [GeneralQueryKeys.EMPLOYEE_NAMES_ONLY],
        queryFn: () => getEmployeeNamesOnly(),
    })

    // update selected keycollection
    const handleSelectKeycollection = (keycollection: any) => {
        setSelectedKeycollection(keycollection)
        if (typeof keycollection !== "boolean") {
            form.setFieldsValue(keycollection)
            if (keycollection.currentOwner === "Hausverwaltung" || keycollection.currentOwner === "Mieter" || keycollection.currentOwner === "Büro") {
                setCurrentOwnerState("extern")
                form.setFieldsValue({ currentOwnerState: "extern" })
            } else {
                setCurrentOwnerState("intern")
                form.setFieldsValue({ currentOwnerState: "intern" })
            }
        }
    }

    // handle new keycollection
    const handleNewKeycollection = () => {
        setSelectedKeycollection(true)
        form.resetFields()
    }

    // handle save keycollection
    const queryClient = useQueryClient()
    const { mutate: addKeycollection } = useAddKeycollectionMutation(queryClient, props.queryKey)
    const { mutate: updateKeycollection } = useUpdateKeycollectionMutation(queryClient, props.queryKey)
    const { mutate: deleteKeycollection } = useDeleteKeycollectionMutation(queryClient, props.queryKey)
    const handleSaveKeycollection = () => {
        form.submit()
        if (selectedKeycollection === true) {
            addKeycollection({
                body: {
                    ...form.getFieldsValue(),
                    signature: signatureRef.current.toDataURL(),
                },
                objectId: props.objectId,
            })
            form.resetFields()
            setSelectedKeycollection(null)
            setSignature(false)
        } else {
            updateKeycollection({
                body: { ...form.getFieldsValue(), signature: signature ? signatureRef.current.toDataURL() : undefined },
                objectId: props.objectId,
                keycollectionId: selectedKeycollection._id,
            })
            form.resetFields()
            setSelectedKeycollection(null)
            setSignature(false)
        }
    }

    //  delete keycollection
    const handleDeleteKeycollection = () => {
        deleteKeycollection({ objectId: props.objectId, keycollectionId: selectedKeycollection._id })
        form.resetFields()
        setSelectedKeycollection(null)
        setSignature(false)
    }

    return (
        <BaseModal
            title={"Schlüssel Management"}
            modalId={props.modalId}
            bottomRow={
                <div className="flex justify-end">
                    <button className="btn btn-primary" onClick={handleSaveKeycollection}>
                        Speichern
                    </button>
                </div>
            }
            onClose={() => {
                setSignature(false)
                form.resetFields()
                setSelectedKeycollection(null)
            }}>
            {selectedKeycollection ? (
                <Form form={form}>
                    <div className="flex flex-col pt-2">
                        <div className="flex justify-between w-full">
                            <p className="flex justify-start pb-2 text-xs hover:underline cursor-pointer" onClick={() => setSelectedKeycollection(null)}>
                                <ArrowLeft className="w-4 h-4 cursor-pointer pr-0.5 mr-1" />
                                Abbrechen
                            </p>
                            <p
                                className="flex justify-start pb-2 text-xs hover:underline cursor-pointer"
                                onClick={handleDeleteKeycollection}>
                                <Trash className="w-4 h-4 cursor-pointer pr-0.5 mr-1" />
                                Schlüsselbund löschen
                            </p>
                        </div>
                    </div>
                    <BaseSectionHeadline label="Schlüsselbund" />
                    <div className="grid grid-cols-2 gap-x-4">
                        <Form.Item name="name" className="!mb-2" rules={[{ required: true, message: "Muss einen Wert enthalten!" }]}>
                            <BaseInputField type="text" label="Name" value={selectedKeycollection.name} onChange={() => {}} />
                        </Form.Item>
                        <Form.Item name="keyCount" className="!mb-2" rules={[{ required: true, message: "Muss einen Wert enthalten!" }]}>
                            <BaseInputField type="text" label="Anzahl" onChange={() => {}} />
                        </Form.Item>
                        <Form.Item name="keys" className="!mb-2" rules={[{ required: true, message: "Muss einen Wert enthalten!" }]}>
                            <BaseInputField type="text" label="Schlüssel" onChange={() => {}} />
                        </Form.Item>
                        <Form.Item name="currentOwnerState" className="!mb-2" rules={[{ required: true, message: "Muss einen Wert enthalten!" }]}>
                            <BaseSelection
                                label="Benutzer"
                                placeholder="Benutzerklasse auswählen"
                                data={["intern", "extern"]}
                                setId={(e) => {
                                    setCurrentOwnerState(e)
                                    form.resetFields(["currentOwner"])
                                    form.setFieldsValue({ currentOwnerState: e })
                                }}
                            />
                        </Form.Item>
                        <Form.Item name="currentOwner" className="!mb-2" rules={[{ required: true, message: "Muss einen Wert enthalten!" }]}>
                            {currentOwnerState === "extern" ? (
                                <BaseSelection
                                    label="Benutzer"
                                    placeholder="Benutzer auswählen"
                                    data={["Hausverwaltung", "Mieter", "Büro"]}
                                    setId={(e) => {
                                        form.setFieldsValue({ currentOwner: e })
                                    }}
                                />
                            ) : (
                                <BaseInputSelection
                                    label="Benutzer"
                                    placeholder="Benutzer auswählen"
                                    data={employees as InputObject[]}
                                    disabled={employees?.length === 0 || !employees}
                                    setId={(e) => {
                                        form.setFieldsValue({ currentOwner: e })
                                    }}
                                />
                            )}
                        </Form.Item>
                    </div>
                    <div className="flex flex-row gap-4 mt-4 w-full">
                        <div className="flex flex-col items-center justify-center w-full max-w-[50%]">
                            <BaseSectionHeadline
                                label="Unterschrift"
                                icon={
                                    !signature ? (
                                        <RefreshCcw className="cursor-pointer w-5 h-5" onClick={() => setSignature(true)} />
                                    ) : (
                                        <X className="cursor-pointer w-5 h-5" onClick={() => setSignature(false)} />
                                    )
                                }
                            />
                            <div className="h-full ">
                                {selectedKeycollection.signature && !signature ? (
                                    <img src={selectedKeycollection.signature} alt="Unterschrift" className="w-[300px] h-[200px] object-cover" />
                                ) : (
                                    <SignatureCanvas ref={signatureRef} canvasProps={{ width: 300, height: 200, className: "signature-canvas border rounded-default" }} />
                                )}
                            </div>
                        </div>
                        {typeof selectedKeycollection !== "boolean" && (
                            <div className="flex flex-col gap-2 w-full max-w-[50%]">
                                <BaseSectionHeadline label="History" />
                                <div className="overflow-y-auto h-64">
                                    {selectedKeycollection?.history?.reverse()?.map((history: any) => (
                                        <div className="flex flex-col mt-2" key={history.timestamp}>
                                            <p className="text-xs text-gray-400">
                                                {dayjs(history.timestamp).format("DD.MM.YYYY")} um {dayjs(history.timestamp).format("HH:mm")} Uhr
                                            </p>
                                            <p className="font-medium text-xs tracking-tight leading-4">{history.change}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Form>
            ) : (
                <>
                    <table className="table table-zebra table-pin-cols">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Anzahl</th>
                                <th>Schlüssel</th>
                                <th>Benutzer</th>
                                <th>Unterschrift</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.keycollections?.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="text-center">
                                        Keine Schlüsselbünde gefunden
                                    </td>
                                </tr>
                            ) : (
                                props.keycollections?.map((keycollection) => (
                                    <tr
                                        key={keycollection._id}
                                        className="h-12 text-xs cursor-pointer hover:bg-gray-100 transition-all duration-300"
                                        onClick={() => handleSelectKeycollection(keycollection)}>
                                        <td>{keycollection.name}</td>
                                        <td>{keycollection.keyCount}</td>
                                        <td>
                                            <p className="line-clamp-2">{keycollection.keys}</p>
                                        </td>
                                        <td>
                                            {employees?.find((employee: { _id: string }) => employee._id === keycollection.currentOwner)?.first_name +
                                                " " +
                                                employees?.find((employee: { _id: string }) => employee._id === keycollection.currentOwner)?.last_name || keycollection.currentOwner}
                                        </td>
                                        <td>{keycollection.signature ? "Unterschrieben" : "Nicht unterschrieben"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    <div onClick={handleNewKeycollection} className="flex flex-row items-center gap-2 text-sm border-b border-white hover:border-black w-fit cursor-pointer">
                        <Plus className="w-4 h-4" />
                        <p>Schlüsselbund hinzufügen</p>
                    </div>
                </>
            )}
        </BaseModal>
    )
}
